import { fork, put, select, call } from 'redux-saga/effects'

import { GOOD_NETWORK_MIN_THRESHOLD_VALUE } from 'constants/config'
import { showElement, hideElement } from 'helpers/dom'
import { getBadNetworkSelector } from 'helpers/elementSelectors'
import { isNil } from 'helpers/lodash'
import { isString } from 'helpers/miscellaneous'
import {
    classNotification,
    CLASS_NOTIFICATION_TYPES,
} from 'helpers/notification'
import {
    getInfoFromIdentity,
    extractAttributesForParticipant,
    isAudioTrack,
    isVideoTrack,
    isScreenShareTrack,
    isDataTrack,
    isTeacher,
    isGhost,
    // toggleVideoTrackMediaOnVisibilityChange,
} from 'helpers/twilio'
import {
    unsubscribeFromAllParticipantsVideoExceptTeacher,
    switchToBandwidthSaverMode,
    switchToNormalMode,
} from 'services/apis/twilioSubscriptions'
import {
    setParticipant,
    removeParticipant,
    updateDominantSpeaker,
    setRemoteParticipants,
    updateParticipantInfo,
    setDisconnectedParticipant,
    setConnectedParticipant,
    updateNetworkBandwidthSaverMode,
} from 'store/actions/Room'
import {
    _dominantSpeaker,
    _isNetworkBandwidthSaverMode,
} from 'store/selectors/Room'
import avatarStore from 'utils/avatarStore'
import { isDesktop } from 'utils/device'

import {
    watchNetworkQualityLevelChanged,
    watchParticipantTrackSubscribed,
    watchParticipantTrackUnsubscribed,
    watchParticipantTrackState,
} from '../watchers/Room'

function* handleLocalParticipantConnected(localParticipant) {
    const participantData = extractAttributesForParticipant(localParticipant)

    yield put(setParticipant({ ...participantData, name: 'You' }))

    yield fork(watchNetworkQualityLevelChanged, localParticipant, true)
}

function* handleParticipantConnected(
    participant,
    { roomSid, localParticipantSid }
) {
    const participantData = extractAttributesForParticipant(participant)

    if (!isDesktop && isTeacher(participant.identity)) {
        yield unsubscribeFromAllParticipantsVideoExceptTeacher(
            roomSid,
            localParticipantSid,
            participant.sid
        )
    }

    if (!isGhost(participant?.identity)) {
        if (isDesktop) {
            yield call(classNotification, {
                type: CLASS_NOTIFICATION_TYPES.call_joined,
                data: {
                    user: participantData.firstName,
                },
            })
        }

        yield put(setConnectedParticipant(participant))
        yield put(setParticipant(participantData))
        yield call(avatarStore.populate, [participantData.id])
    }

    yield fork(watchNetworkQualityLevelChanged, participant)
    yield fork(watchParticipantTrackSubscribed, participant)
    yield fork(watchParticipantTrackUnsubscribed, participant)
}

function* handleRemoteParticipants(remoteParticipants) {
    const remoteParticipantsData = []

    for (let remoteParticipant of remoteParticipants.values()) {
        if (!isGhost(remoteParticipant?.identity)) {
            remoteParticipantsData.push(
                extractAttributesForParticipant(remoteParticipant)
            )
        }

        yield fork(watchNetworkQualityLevelChanged, remoteParticipant)
        yield fork(watchParticipantTrackSubscribed, remoteParticipant)
        yield fork(watchParticipantTrackUnsubscribed, remoteParticipant)
    }

    yield put(setRemoteParticipants(remoteParticipantsData))
}

function* handleParticipantDisconnected(participant) {
    const { identity } = participant
    const participantInfo = getInfoFromIdentity(identity)

    if (!participantInfo.isGhost) {
        if (isDesktop) {
            yield call(classNotification, {
                type: CLASS_NOTIFICATION_TYPES.call_left,
                data: {
                    user: participantInfo.firstName,
                },
            })
        }

        yield put(setDisconnectedParticipant(participant))
        yield put(removeParticipant(participantInfo))

        /**
         * now check if this participant who got disconnected
         * was a dominant speaker, if so then set it to null
         */

        const dominantSpeaker = yield select(_dominantSpeaker)

        if (
            isString(dominantSpeaker) &&
            dominantSpeaker === participantInfo.id
        ) {
            yield put(updateDominantSpeaker(null))
        }
    }
}

function* handleDominantSpeakerChanged(participant) {
    if (participant) {
        const { identity } = participant

        const { id } = getInfoFromIdentity(identity)

        yield put(updateDominantSpeaker(id))
    } else {
        /**
         * here participant is probably null
         * so first select its previous state and set null if its not already
         */
        const dominantSpeaker = yield select(_dominantSpeaker)

        if (!isNil(dominantSpeaker)) {
            yield put(updateDominantSpeaker(null))
        }
    }
}

function* handleNetworkQualityLevelChanged(
    { identity, level },
    isLocalUser = false
) {
    const { id } = getInfoFromIdentity(identity)

    const elementSelectorQuery = getBadNetworkSelector(id)

    // const isNetworkBandwidthSaverMode = yield select(
    //     _isNetworkBandwidthSaverMode
    // )

    if (Number.isInteger(level) && level <= GOOD_NETWORK_MIN_THRESHOLD_VALUE) {
        // show indicator
        showElement(elementSelectorQuery)

        // if (isLocalUser && !isNetworkBandwidthSaverMode) {
        //     try {
        //         yield call(switchToBandwidthSaverMode, identity)
        //         yield put(updateNetworkBandwidthSaverMode(true))
        //     } catch (error) {
        //         console.log('error in switching to bandwidth saver mode', error)
        //     }
        // }
    } else {
        // hide indicator
        hideElement(elementSelectorQuery)

        // if (isLocalUser && isNetworkBandwidthSaverMode) {
        //     try {
        //         yield call(switchToNormalMode, identity)
        //         yield put(updateNetworkBandwidthSaverMode(false))
        //     } catch (error) {
        //         console.log('error in switching to normal mode', error)
        //     }
        // }
    }
}

function* handleParticipantTrackSubscribed({ identity, track }) {
    const { id } = getInfoFromIdentity(identity)
    const { isEnabled, isSwitchedOff, kind, name } = track

    if (isDataTrack(kind)) return

    if (isEnabled && !isSwitchedOff) {
        if (isVideoTrack(kind, name)) {
            yield put(updateParticipantInfo(id, 'isVideoEnabled', true))

            // switch on / off media based on visibility
            // !isTeacher(identity) && toggleVideoTrackMediaOnVisibilityChange(id)
        } else if (isScreenShareTrack(kind, name)) {
            yield put(updateParticipantInfo(id, 'isScreenSharing', true))
        } else if (isAudioTrack(kind, name)) {
            yield put(updateParticipantInfo(id, 'isAudioEnabled', true))

            // now watch here for audio track enabled disabled state
            yield fork(watchParticipantTrackState, identity, track)
        }
    }
}

function* handleParticipantTrackUnsubscribed({ identity, track }) {
    const { id } = getInfoFromIdentity(identity)
    const { kind, name } = track

    if (isDataTrack(kind)) return

    if (isAudioTrack(kind, name)) {
        yield put(updateParticipantInfo(id, 'isAudioEnabled', false))
    } else if (isVideoTrack(kind, name)) {
        yield put(updateParticipantInfo(id, 'isVideoEnabled', false))
    } else if (isScreenShareTrack(kind, name)) {
        yield put(updateParticipantInfo(id, 'isScreenSharing', false))
    }
}

function* handleParticipantTrackState({ identity, track, enabled }) {
    const { id } = getInfoFromIdentity(identity)
    const { kind, name } = track

    /**
     * currently only handling audio track state , as for video and screenshare
     * track get subscribed / unsubscribed on toggling unlike with audio
     * and that is already being handled
     */
    if (isAudioTrack(kind, name)) {
        yield put(updateParticipantInfo(id, 'isAudioEnabled', enabled))
    }
}

export {
    handleLocalParticipantConnected,
    handleParticipantConnected,
    handleParticipantDisconnected,
    handleDominantSpeakerChanged,
    handleRemoteParticipants,
    handleNetworkQualityLevelChanged,
    handleParticipantTrackSubscribed,
    handleParticipantTrackUnsubscribed,
    handleParticipantTrackState,
}
